import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useBookings, outcomeOf, whoIsGoing, type BookedLesson } from '../api/use-bookings.js';
import { lessonLink } from '../api/lesson-link.js';
import { WaitNote } from '../components/wait-note.js';

/**
 * Lessons — every person on the account, in one list.
 *
 * THREE WAYS TO READ ONE LIST, not a filter. Nothing is hidden by the choice:
 * the same lessons are present in all three, arranged differently. Date is the
 * default because what is coming is what a parent opens the app for.
 *
 * NO HEADINGS ON THE DATE VIEW. The chronology carries it — booked and not yet
 * completed is always at the top, and each row's outcome says the rest. Person
 * and Type do carry headings, because there the grouping IS the arrangement.
 *
 * TYPE MEANS THE PRODUCT'S NAME — One to one, Group of 2 — not a hardcoded
 * private/group pair. Products are configuration, so a new one appears in the
 * sort by being created.
 */

type Sort = 'Date' | 'Person' | 'Type';

export function LessonsRoute() {
  const { state } = useBookings();
  const [sort, setSort] = useState<Sort>('Date');

  if (state.status === 'loading') return <WaitNote className="empty">Checking…</WaitNote>;
  if (state.status === 'failed') return <p className="empty">{state.message}</p>;

  if (state.lessons.length === 0) {
    return (
      <>
        <p className="empty">No lessons yet.</p>
        <Link className="primary" to="/booking">
          Book a lesson
        </Link>
      </>
    );
  }

  return (
    <>
      <div className="sorter" role="group" aria-label="Sort by">
        {(['Date', 'Person', 'Type'] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={sort === option}
            onClick={() => setSort(option)}
          >
            {option}
          </button>
        ))}
      </div>

      {sort === 'Date'
        ? state.lessons.map((lesson) => <LessonRow key={lesson.booking_id} lesson={lesson} showWho />)
        : groupsFor(sort, state.lessons).map(([heading, lessons]) => (
            <div key={heading} className="day-group">
              <span className="section-label">{heading}</span>
              {lessons.map((lesson) => (
                <LessonRow
                  key={`${heading}-${lesson.booking_id}`}
                  lesson={lesson}
                  showWho={sort !== 'Person'}
                />
              ))}
            </div>
          ))}
    </>
  );
}

function LessonRow({ lesson, showWho }: { lesson: BookedLesson; showWho: boolean }) {
  const outcome = outcomeOf(lesson);
  const who = whoIsGoing(lesson).map((attendee) => attendee.name.split(' ')[0]).join(' and ');

  return (
    <Link className="hour-row" to={lessonLink(lesson)}>
      <span className="hour-row-text">
        <span className="name">{when(lesson.slot_start)}</span>
        <span className="detail">
          {[showWho ? who : null, `Coach ${lesson.coach_name}`, lesson.product_name]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
      <span className={`outcome ${outcome.replace(' ', '-').toLowerCase()}`}>{outcome}</span>
      <span className="chevron" aria-hidden="true">
        ›
      </span>
    </Link>
  );
}

/**
 * One lesson can appear under several people — a group of two is two athletes —
 * so grouping by person repeats the row under each of them. That is the honest
 * answer to "show me Noa's lessons", not a bug.
 */
function groupsFor(sort: Sort, lessons: BookedLesson[]): [string, BookedLesson[]][] {
  const groups = new Map<string, BookedLesson[]>();

  for (const lesson of lessons) {
    const keys =
      sort === 'Person'
        ? lesson.attendees.map((attendee) => attendee.name)
        : [lesson.product_name ?? lesson.service_name];

    for (const key of keys.length > 0 ? keys : ['Nobody named']) {
      groups.set(key, [...(groups.get(key) ?? []), lesson]);
    }
  }

  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}
